import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface UserStatus {
  isAdmin: boolean;
  isApproved: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getUserStatus: () => Promise<UserStatus>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active sessions and sets the user
    const initSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    initSession();

    // Listen for changes on auth state (only update if user actually changed)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(prev => {
        const newUser = session?.user ?? null;
        // Only update state if the user ID actually changed (avoids re-renders on token refresh)
        if (prev?.id === newUser?.id) return prev;
        return newUser;
      });
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getUserStatus = async (): Promise<UserStatus> => {
    if (!user) {
      console.log('getUserStatus: No user found');
      return { isAdmin: false, isApproved: false };
    }

    try {
      console.log('getUserStatus: Checking status for user ID:', user.id);
      
      // First try the user_profiles table
      let { data, error } = await supabase
        .from('user_profiles')
        .select('is_admin, is_approved')
        .eq('id', user.id)
        .single();

      // If there's an error or no data, check the profiles table instead
      if (error || !data) {
        console.log('getUserStatus: Trying profiles table instead...');
        
        // Check if the profiles table has the required columns
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (!profileError && profileData) {
          console.log('getUserStatus: Found user in profiles table:', profileData);
          
          // Check if the profiles table has the required columns
          if ('is_admin' in profileData && 'is_approved' in profileData) {
            data = {
              is_admin: profileData.is_admin,
              is_approved: profileData.is_approved
            };
          } else {
            // Create user_profiles entry from profiles data
            console.log('getUserStatus: Creating user_profiles entry from profiles data');
            
            // Special case for the admin user
            if (user.email === 'jamesperrin1999@gmail.com') {
              const profile = {
                id: user.id,
                email: user.email,
                is_admin: true,
                is_approved: true,
                created_at: new Date().toISOString()
              };
              
              const { error: insertError } = await supabase
                .from('user_profiles')
                .insert(profile);
                
              if (insertError) {
                console.error('Error creating admin profile:', insertError);
              }
              
              return { isAdmin: true, isApproved: true };
            } else {
              // Try to create a regular user profile
              const profile = {
                id: user.id,
                email: user.email,
                is_admin: false,
                is_approved: true, // Default to approved for existing profiles
                created_at: new Date().toISOString()
              };
              
              const { error: insertError } = await supabase
                .from('user_profiles')
                .insert(profile);
                
              if (insertError) {
                console.error('Error creating user profile from profiles:', insertError);
                return { isAdmin: false, isApproved: false };
              }
              
              return { isAdmin: false, isApproved: true };
            }
          }
        } else {
          // Special case for hardcoded admin
          if (user.email === 'jamesperrin1999@gmail.com') {
            console.log('Detected special admin user - creating admin profile');
            // Create admin profile
            try {
              const profile = {
                id: user.id,
                email: user.email,
                is_admin: true,
                is_approved: true,
                created_at: new Date().toISOString()
              };
              
              console.log('Creating admin profile:', profile);
              
              const { error: profileError } = await supabase
                .from('user_profiles')
                .insert(profile);
                
              if (profileError) {
                console.error('Error creating admin profile:', profileError);
                return { isAdmin: true, isApproved: true }; // Still return admin privileges
              }
              
              return { isAdmin: true, isApproved: true };
            } catch (err) {
              console.error('Error creating admin profile:', err);
              return { isAdmin: true, isApproved: true }; // Still return admin privileges
            }
          }
          
          // For all other users, they need to go through the signup flow
          console.log('No profile found and not an admin - user needs to sign up properly');
          return { isAdmin: false, isApproved: false };
        }
      }

      console.log('getUserStatus: Raw data received:', data);
      
      // Handle potential string values from JSON
      const isAdmin = data.is_admin === true || data.is_admin === 'true';
      const isApproved = data.is_approved === true || data.is_approved === 'true';
      
      console.log('getUserStatus: Processed values:', { isAdmin, isApproved });

      return { isAdmin, isApproved };
    } catch (err) {
      console.error('Error in getUserStatus:', err);
      return { isAdmin: false, isApproved: false };
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    getUserStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 